import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Archive, Trash2, Ban, X } from "lucide-react";
import { mailApi } from "./mailApi";
import { MailSenderRow } from "./MailSenderRow";

const LABEL = { archive: "Archiviate", trash: "Cestinate", block: "Bloccate" };

// Sender list with search, multi-select and bulk actions (archive / trash / block).
export function MailSenders({ kind, onChanged }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = () => mailApi.senders(kind, q).then(setRows).catch(() => toast.error("Errore caricamento mittenti"));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [kind, q]); // eslint-disable-line

  const toggle = (em) => setSel((s) => { const n = new Set(s); n.has(em) ? n.delete(em) : n.add(em); return n; });
  const act = async (emails, action) => {
    const n = rows.filter((r) => emails.includes(r.email)).reduce((a, r) => a + r.count, 0);
    const msg = action === "block"
      ? `Bloccare ${emails.length} mittente/i? Proverò a disiscriverti, sposterò nel Cestino le ${n} mail presenti e anche quelle future.`
      : `${action === "archive" ? "Archiviare" : "Spostare nel Cestino"} ${n} mail di ${emails.length} mittente/i?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await mailApi.sendersAction(emails, action);
      const moved = Object.values(r.moved || {}).reduce((a, b) => a + b, 0);
      const un = Object.values(r.unsubscribe || {});
      const auto = un.filter((u) => u.ok).length;
      const links = un.filter((u) => !u.ok && u.link);
      toast.success(`${LABEL[action]} ${moved} mail${action === "block" ? ` · disiscrizione automatica: ${auto}/${un.length}` : ""}`);
      links.forEach((u) => window.open(u.link, "_blank", "noopener"));
      setSel(new Set());
      load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#9a7f72]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca mittente, email o oggetto…" className="w-full rounded-lg border border-[#e8dcc8] bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/20" data-testid="mail-senders-search" />
      </div>
      {sel.size > 0 && (
        <div className="sticky top-0 z-10 mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-[#2b1410] text-[#f7ebdf] px-3 py-2 text-sm" data-testid="mail-bulk-bar">
          <span className="font-semibold">{sel.size} selezionati</span>
          <button disabled={busy} onClick={() => act([...sel], "archive")} className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1" data-testid="mail-bulk-archive"><Archive className="h-3.5 w-3.5" /> Archivia</button>
          <button disabled={busy} onClick={() => act([...sel], "trash")} className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1" data-testid="mail-bulk-trash"><Trash2 className="h-3.5 w-3.5" /> Cestina</button>
          <button disabled={busy} onClick={() => act([...sel], "block")} className="inline-flex items-center gap-1 rounded-full bg-red-600 hover:bg-red-700 px-3 py-1" data-testid="mail-bulk-block"><Ban className="h-3.5 w-3.5" /> Blocca + disiscrivi</button>
          <button onClick={() => setSel(new Set())} className="ml-auto p-1 rounded hover:bg-white/10"><X className="h-4 w-4" /></button>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        </div>
      )}
      {!rows ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#4a1c17]" /></div>
        : rows.length === 0 ? <p className="py-8 text-center text-sm text-[#7c5a4e]" data-testid="mail-senders-empty">Nessun mittente. Avvia prima l'analisi della casella.</p>
        : (
          <div className="rounded-xl border border-[#e8dcc8] overflow-hidden" data-testid="mail-senders-list">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#faf3ee] text-xs text-[#7c5a4e]">
              <button onClick={() => setSel(new Set(rows.map((r) => r.email)))} className="underline" data-testid="mail-select-all">Seleziona tutti ({rows.length})</button>
            </div>
            {rows.map((s) => <MailSenderRow key={s.email} s={s} selected={sel.has(s.email)} onToggle={() => toggle(s.email)} onAction={act} busy={busy} />)}
          </div>
        )}
    </div>
  );
}
